import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  useNotify
} from '@globaleris/ui.library'
import { Box, makeStyles, Typography, useTheme } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AtlasAccessDetailModel,
  AtlasEnabledEmailModel,
  AtlasEnabledSubjectModel,
  AtlasRegisteredUserModel,
  EnumAtlasAccessScope,
  EnumAtlasEntityType
} from '../../../../../api/Argentia/endpoints/atlasAccess/model/atlasAccess'
import SimpleDialog from '../../../../../components/shared/simpleDialog/simpleDialog'
import TabsWithPermissions, {
  Tab
} from '../../../../../components/tabsWithPermissions/tabsWithPermissions'
import { getCustomerDomain } from '../../../../../redux'
import { fetchClientAccessDetail } from '../../atlasAccess.service'
import { ClientAccessModel } from '../clientsAccessTable/clientsAccessTable'
import { styles } from './clientDetailAccessDialog.styles'

interface ClientDetailAccessDialogProps {
  open: boolean
  entity?: ClientAccessModel
  onClose: () => void
}

enum EnumClientDetailAccessTab {
  RegisteredUsers = 0,
  // emails habilitados si el sujeto es cliente, clientes con acceso si es comercial
  SecondTab = 1
}

export const ClientDetailAccessDialog: React.FC<ClientDetailAccessDialogProps> = ({
  open,
  entity,
  onClose
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'atlasAccess'])
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  const { error } = useNotify()

  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [detail, setDetail] = useState<AtlasAccessDetailModel | undefined>(undefined)
  const [activeTab, setActiveTab] = useState<EnumClientDetailAccessTab>(
    EnumClientDetailAccessTab.RegisteredUsers
  )

  const isComercial = entity?.entityType === EnumAtlasEntityType.Commercial

  // #region Functions Starts
  const getAccessTypeLabel = (accessType: EnumAtlasAccessScope) =>
    t(
      `atlasAccess:components.clientDetailAccessDialog.accessTypes.${accessType === EnumAtlasAccessScope.Client ? 'client' : accessType === EnumAtlasAccessScope.Contract ? 'contract' : 'cups'}`
    )
  // #endregion Functions Ends

  // #region Hooks Starts
  const fetchDetail = async () => {
    if (!entity) {
      return
    }
    setIsFetching(true)
    await fetchClientAccessDetail(entity.entityId, entity.entityType)
      .then(response => {
        if (response.success) {
          setDetail(response.data)
        } else {
          error(t('atlasAccess:notifications.clientDetail.error'))
        }
      })
      .finally(() => setIsFetching(false))
  }

  useEffect(() => {
    if (open) {
      setActiveTab(EnumClientDetailAccessTab.RegisteredUsers)
      setDetail(undefined)
      fetchDetail()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entity])
  // #endregion Hooks Ends

  // #region DataTable Config Starts
  const registeredUsersData = (detail?.registeredUsers ?? []).map(
    (row: AtlasRegisteredUserModel) => ({
      ...row,
      accessLevelLabel: t(
        `atlasAccess:components.clientDetailAccessDialog.accessLevels.${row.accessLevel === 'DIRECTO' ? 'direct' : 'commercial'}`
      ),
      accessTypeLabel: getAccessTypeLabel(row.accessType)
    })
  )

  const registeredUsersColumns: DataTableColumn[] = [
    {
      name: 'userEmail',
      label: t('atlasAccess:components.clientDetailAccessDialog.columns.userEmail'),
      type: DataType.TEXT
    },
    {
      name: 'accessLevelLabel',
      label: t('atlasAccess:components.clientDetailAccessDialog.columns.accessLevel'),
      type: DataType.TEXT
    },
    {
      name: 'accessTypeLabel',
      label: t('atlasAccess:components.clientDetailAccessDialog.columns.accessType'),
      type: DataType.TEXT
    },
    {
      name: 'contractDescription',
      label: t('atlasAccess:components.clientDetailAccessDialog.columns.contractDescription'),
      type: DataType.TEXT
    },
    {
      name: 'cups',
      label: t('atlasAccess:components.clientDetailAccessDialog.columns.cups'),
      type: DataType.TEXT
    }
  ]

  const enabledEmailsData = (detail?.enabledEmails ?? []).map((row: AtlasEnabledEmailModel) => ({
    ...row,
    contactTypeLabel: getAccessTypeLabel(row.contactType)
  }))

  const enabledEmailsColumns: DataTableColumn[] = [
    {
      name: 'email',
      label: t('atlasAccess:components.clientDetailAccessDialog.columns.enabledEmail'),
      type: DataType.TEXT
    },
    {
      name: 'contactTypeLabel',
      label: t('atlasAccess:components.clientDetailAccessDialog.columns.contactType'),
      type: DataType.TEXT
    },
    {
      name: 'contractDescription',
      label: t('atlasAccess:components.clientDetailAccessDialog.columns.contractDescription'),
      type: DataType.TEXT
    },
    {
      name: 'cups',
      label: t('atlasAccess:components.clientDetailAccessDialog.columns.cups'),
      type: DataType.TEXT
    }
  ]

  const enabledSubjectsData: AtlasEnabledSubjectModel[] = detail?.enabledSubjects ?? []

  const enabledSubjectsColumns: DataTableColumn[] = [
    {
      name: 'subjectId',
      display: false
    },
    {
      name: 'subjectName',
      label: t('atlasAccess:components.clientDetailAccessDialog.columns.subjectName'),
      type: DataType.TEXT
    }
  ]

  const getTableOptions = (analyticsView: string): DataTableOptions => ({
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 0,
    filterType: FilterType.ADVANCED,
    download: false,
    canEdit: false,
    canDelete: false,
    canInsert: false,
    pagination: true,
    analytics: {
      customer: getCustomerDomain(),
      view: analyticsView
    }
  })
  // #endregion DataTable Config Ends

  const tabsDefinition = new Map<number, Tab>([
    [
      EnumClientDetailAccessTab.RegisteredUsers,
      {
        label: t('atlasAccess:components.clientDetailAccessDialog.tabs.registeredUsers'),
        hasPermission: true
      }
    ],
    [
      EnumClientDetailAccessTab.SecondTab,
      {
        label: t(
          `atlasAccess:components.clientDetailAccessDialog.tabs.${isComercial ? 'enabledSubjects' : 'enabledEmails'}`
        ),
        hasPermission: true
      }
    ]
  ])

  return (
    <SimpleDialog
      open={open}
      handleClose={onClose}
      title={t('atlasAccess:components.clientDetailAccessDialog.title')}
      footerActions={[
        {
          label: t('atlasAccess:components.clientDetailAccessDialog.buttons.close'),
          handleActionClick: onClose
        }
      ]}
    >
      <Box className={classes.headerInfo}>
        <Typography variant="h6">{detail?.entityName ?? entity?.entityName}</Typography>
        <Typography variant="body2" className={classes.headerCif}>
          {detail?.entityCif}
        </Typography>
      </Box>

      <Box className={classes.tabsWrapper}>
        <TabsWithPermissions
          tabs={tabsDefinition}
          tabDefault={activeTab}
          onTabChange={tab => setActiveTab(tab)}
        />
      </Box>

      <Box className={classes.tableWrapper}>
        {activeTab === EnumClientDetailAccessTab.RegisteredUsers ? (
          <DataTable
            data={isFetching ? undefined : registeredUsersData}
            columns={registeredUsersColumns}
            options={getTableOptions('atlas_access_client_detail_registered_users_table')}
          />
        ) : isComercial ? (
          <DataTable
            data={isFetching ? undefined : enabledSubjectsData}
            columns={enabledSubjectsColumns}
            options={getTableOptions('atlas_access_client_detail_enabled_subjects_table')}
          />
        ) : (
          <DataTable
            data={isFetching ? undefined : enabledEmailsData}
            columns={enabledEmailsColumns}
            options={getTableOptions('atlas_access_client_detail_enabled_emails_table')}
          />
        )}
      </Box>
    </SimpleDialog>
  )
}
